import React, {
    Dispatch, SetStateAction, useContext, useMemo,
} from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import {
    payLaterButtonText,
    payNowButtonText,
    RapidStorSettings,
    saveQuoteButtonText,
} from '@/core/utils/rapidstor';

import Button from '@/core/components/elements/button';
import { FormType } from '@/core/components/elements/contact-card';
import Spinner from '@/core/components/elements/spinner';

import {
    ANIMATION_DELAY,
    ANIMATION_SPEED,
    optionsCardVariants,
} from '@/features/rapidstor/animations';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import classes from './CardButtons.module.scss';

interface Props {
    isUpsell?: boolean;
    isLoading?: boolean;
    cardState: string;
    deal: string | null;
    toggleContactCard: (
        type: FormType,
        selectedUnitType?: UnitType,
        deal?: string
    ) => void;
    makeReservation: () => void;
    selectUpsellCard: () => void;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    desktop?: boolean;
    options: UnitType[];
    unitType: UnitType;
}

export default function CardButtons({
    isUpsell,
    isLoading,
    cardState,
    toggleContactCard,
    makeReservation,
    selectUpsellCard,
    setIsLoading,
    desktop,
    options,
    deal,
    unitType,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const { unavailableDescriptorIds, unitData } = useContext(JourneyContext);

    const emailQuoteButton = () => {
        if (!RapidStorSettings(location).showSaveQuoteButton()) {
            return false;
        }
        if (unitData?.descriptor && unavailableDescriptorIds.includes(unitData?.descriptor._id)) {
            return false;
        }
        if (isUpsell) {
            return false;
        }
        if (desktop && options.length > 1) {
            return false;
        }

        return true;
    };

    const reserveButton = () => {
        if (!RapidStorSettings(location).showReservationButton()) {
            return false;
        }

        if (isUpsell) {
            return false;
        }

        if (desktop && options.length > 1) {
            return false;
        }

        return true;
    };

    const showPayNowOnDesktop = useMemo(() => {
        if (!RapidStorSettings(location).showMoveInButton()) {
            return false;
        }

        if (desktop && !isUpsell && options.length < 2) {
            return true;
        }

        return false;
    }, [desktop, isUpsell, location, options.length]);

    const showPayNowOnMobile = useMemo(() => {
        if (!RapidStorSettings(location).showMoveInButton()) {
            return false;
        }

        if ((!desktop && cardState === 'show') || cardState === 'close') {
            return true;
        }

        return false;
    }, [cardState, desktop, location]);

    const showUpgradeButton = useMemo(() => {
        if (isUpsell) {
            return true;
        }

        return false;
    }, [isUpsell]);

    return (
        <motion.div
            layout
            variants={optionsCardVariants}
            initial="start"
            animate="show"
            transition={{
                duration: ANIMATION_SPEED,
                delay: ANIMATION_DELAY * 2,
            }}
            className={`${classes.buttons}`}
        >
            {emailQuoteButton() && (
                <Button
                    disabled={isLoading}
                    onclick={() => {
                        toggleContactCard('quote', unitType, deal || undefined);
                    }}
                    type="outlineOnPrimary"
                    size="medium"
                    testId="save-quote"
                >
                    {saveQuoteButtonText(location)}
                </Button>
            )}
            {reserveButton() && (
                <Button
                    disabled={isLoading}
                    onclick={() => {
                        toggleContactCard('reservation', unitType, deal || undefined);
                    }}
                    type="outlineOnPrimary"
                    size="medium"
                    testId="quick-reserve"
                >
                    {payLaterButtonText(location)}
                </Button>
            )}
            {(showPayNowOnDesktop || showPayNowOnMobile) && (
                <Button
                    disabled={isLoading}
                    onclick={() => {
                        if (isUpsell) {
                            selectUpsellCard();
                        } else {
                            setIsLoading(true);
                            makeReservation();
                        }
                    }}
                    type={`${isUpsell ? 'primary' : 'textOnPrimary'}`}
                    size="medium"
                    testId="pay-now"
                >
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        payNowButtonText(location)
                    )}
                </Button>
            )}
            {showUpgradeButton && (
                <Button
                    disabled={isLoading}
                    onclick={() => {
                        if (isUpsell) {
                            selectUpsellCard();
                        } else {
                            setIsLoading(true);
                            makeReservation();
                        }
                    }}
                    type={`${isUpsell ? 'primary' : 'textOnPrimary'}`}
                    size="medium"
                    testId="upgrade"
                >
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        <label>{t('customise_booking:upgrade')}</label>
                    )}
                </Button>
            )}
        </motion.div>
    );
}
